import React, { useEffect, useRef } from 'react'
import { BlockComponentProps } from '../../../../doclets/types'
import {
  captionStyle,
  imageBlockStyle
} from '../../../../doclets/blocks/image/ImageBlock'
import InlineTextInput from '../../../../doclets/InlineTextInput'
import useTranslation from '../../../../hooks/useTranslation'
import EditableImage from './EditableImage'

/**
 * Builder editor for the Image block: the picture (EditableImage: drop zone,
 * upload and replace) with its caption edited inline underneath. Alt text,
 * link and full width are in the block settings sidebar.
 */
const ImageBlockEditor = ({
  block,
  onChange,
  onFocus,
  selected = false
}: BlockComponentProps<'image'>) => {
  const { t } = useTranslation()
  // The upload finishes later: merge into the props as they are then, not as
  // they were when it started (the caption may have been edited meanwhile)
  const latest = useRef({ props: block.props, onChange })
  latest.current = { props: block.props, onChange }
  // Tidy the caption when the block is deselected: a spaces-only caption
  // counts as none (so the "Add a caption" prompt comes back), and a real one
  // loses its outer spaces, as the page shows it. Not while typing, so a
  // trailing space doesn't vanish mid-word.
  const wasSelected = useRef(selected)
  useEffect(() => {
    if (wasSelected.current && !selected) {
      const { props, onChange: save } = latest.current
      const tidy = props.caption.trim()
      if (tidy !== props.caption) save?.({ ...props, caption: tidy })
    }
    wasSelected.current = selected
  }, [selected])

  const { caption } = block.props

  return (
    <EditableImage
      block={block}
      onUploaded={uploaded =>
        latest.current.onChange?.({ ...latest.current.props, ...uploaded })
      }
      onFocus={onFocus}
      selected={selected}
      filledStyle={imageBlockStyle}
    >
      {(selected || !!caption.trim()) && (
        <InlineTextInput
          value={caption}
          placeholder={t('doclets:image:captionPlaceholder')}
          accessibilityLabel={t('doclets:image:caption')}
          textStyle={captionStyle(block)}
          onFocus={onFocus}
          onChangeText={value => onChange?.({ ...block.props, caption: value })}
        />
      )}
    </EditableImage>
  )
}

export default ImageBlockEditor
